import type { GroundValue } from "../computation-model/data";
import type { TableName } from "../database-strings";
import { handlerForActionKind } from "../generator/actions";
import type {
    WireActionHydrator,
    WireActionRunner,
    WireComponentHydratorWithID,
    WireHydrationBackend,
    WireInflationBackend,
    WireRowActionHydrationValueProvider,
    WireRowComponentInflationBackend,
    WireRowComponentValueGetter,
} from "../generator/components";
import type {
    ActionDescription,
    ColumnAssignment,
    ComponentDescription,
    FilterArrayTransform,
} from "../generator/description";
import { ArrayTransformKind, isColumnWriteable, isPrimitiveType } from "../generator/description";
import { handlerForComponentKind } from "../generator/handlers";
import { filterType, isArray, mapFilterUndefined } from "../Support";
import type { WireAction } from "../wire/types";

export function isNotEmptyDisplayValue(v: GroundValue | null): boolean {
    return v !== undefined && v !== null && v !== "";
}

export function hydrateAction(
    hydrator: WireActionHydrator | undefined,
    vp: WireRowActionHydrationValueProvider
): [runner: WireActionRunner, url: string | undefined] | undefined {
    if (hydrator === undefined) return undefined;

    const maybeRunner = hydrator(vp);
    if (maybeRunner === undefined) return undefined;

    if (isArray(maybeRunner)) {
        return maybeRunner;
    } else {
        return [maybeRunner, undefined];
    }
}

export function inflateActions(
    ib: WireInflationBackend,
    actions: readonly ActionDescription[]
): WireActionHydrator | undefined {
    const hydrators = mapFilterUndefined(actions, a => {
        const handler = handlerForActionKind(a.kind);
        return handler?.inflate?.(ib, a);
    });
    if (hydrators.length === 0) return undefined;

    return vp => {
        const runners: WireActionRunner[] = [];
        let url: string | undefined;

        for (let i = 0; i < hydrators.length; i++) {
            const hydrator = hydrators[i];

            const maybeRunner = hydrateAction(hydrator, vp);
            if (maybeRunner === undefined) return undefined;

            const [runner, maybeURL] = maybeRunner;
            if (i === 0) {
                url = maybeURL;
            }

            runners.push(runner);
        }
        return [
            async (b, handled) => {
                for (const [i, r] of runners.entries()) {
                    if (!(await r(b, handled && i === 0))) {
                        return false;
                    }
                }
                return true;
            },
            url,
        ];
    };
}

export type OutputValueGetters = readonly [string, WireRowComponentValueGetter][];

export function inflateColumnAssignments(
    ib: WireInflationBackend,
    outputTableName: TableName,
    columnAssignments: readonly ColumnAssignment[],
    allowUserSpecificColumns: boolean
): OutputValueGetters | undefined {
    const outputTable = ib.adc.findTable(outputTableName);
    if (outputTable === undefined) return undefined;

    const targetColumnNames = new Set(
        outputTable.columns.filter(c => isColumnWriteable(c, outputTable, allowUserSpecificColumns)).map(c => c.name)
    );

    const outputValueGetters: [string, WireRowComponentValueGetter][] = [];
    for (const a of columnAssignments) {
        if (!targetColumnNames.has(a.destColumn)) continue;

        const [getter, type] = ib.getValueGetterForProperty(a.value, false);
        if (!isPrimitiveType(type)) continue;
        outputValueGetters.push([a.destColumn, getter]);
    }

    return outputValueGetters;
}

export function inflateComponent(
    ib: WireRowComponentInflationBackend,
    desc: ComponentDescription,
    visibilityFromOutputRow: boolean
): WireComponentHydratorWithID | undefined {
    const handler = handlerForComponentKind(desc.kind);
    const hydrator = handler?.inflate?.(ib, desc);
    if (hydrator === undefined) return undefined;

    const { componentID } = desc;

    const filters = filterType(
        desc.visibilityFilters ?? [],
        (t): t is FilterArrayTransform => t.kind === ArrayTransformKind.Filter
    );
    if (filters.length === 0) {
        return [hydrator, componentID];
    }

    const visibilityPredicate = ib.inflateFilters(filters, visibilityFromOutputRow, undefined);
    return [
        hb => {
            if (!visibilityPredicate(hb)) return undefined;
            return hydrator(hb);
        },
        componentID,
    ];
}

export function registerActionRunner(
    hb: WireHydrationBackend,
    runner: WireActionRunner,
    url: string | undefined
): WireAction {
    const token = hb.registerAction(runner);
    return { token, url };
}
